import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";
import CategoryPricingPicker from "../../Components/CategoryPricingPicker";
import RestaurantSelect from "../../Components/RestaurantSelect";

const EMPTY_FORM = {
  title: "",
  desc: "",
  restaurants: [],
  categoryPricing: [],
  availability: false,
  popular: false,
  imageUrl: [],
};

const CreateItem = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim() || !formData.desc.trim()) {
      setError("Title and description are required.");
      return;
    }
    if (formData.restaurants.length === 0) {
      setError("Pick at least one restaurant.");
      return;
    }
    if (formData.categoryPricing.length === 0) {
      setError("Pick at least one category and set its price.");
      return;
    }
    const categoryPricing = formData.categoryPricing.map((e) => ({
      category: e.category,
      price: Number(e.price),
    }));
    if (categoryPricing.some((e) => Number.isNaN(e.price) || e.price <= 0)) {
      setError("Every selected category needs a positive price.");
      return;
    }
    if (formData.imageUrl.length === 0) {
      setError("Upload at least one image.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE_URL}/menu/create-item`,
        { ...formData, categoryPricing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData(EMPTY_FORM);
      alert("Item created successfully!");
    } catch (err) {
      console.error("Error creating item:", err);
      setError(err.response?.data?.message || "Failed to create item.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <form
        onSubmit={handleSubmit}
        className="bg-card border border-line rounded-lg p-6 w-full max-w-2xl mx-auto space-y-4"
      >
        <h2 className="text-2xl font-bold text-ink">Create New Item</h2>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}

        <div>
          <label className="block font-semibold mb-1 text-ink">Title</label>
          <input
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter item title"
            className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1 text-ink">Description</label>
          <input
            name="desc"
            value={formData.desc}
            onChange={handleChange}
            placeholder="Enter description"
            className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
            required
          />
        </div>

        <RestaurantSelect
          value={formData.restaurants}
          onChange={(restaurants) => setFormData({ ...formData, restaurants })}
        />

        <CategoryPricingPicker
          value={formData.categoryPricing}
          onChange={(categoryPricing) => setFormData({ ...formData, categoryPricing })}
        />

        <ImageUpload
          multiple
          label="Images"
          value={formData.imageUrl}
          onChange={(imageUrl) => setFormData({ ...formData, imageUrl })}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            name="availability"
            checked={formData.availability}
            onChange={handleChange}
            className="mr-2"
          />
          <label className="font-semibold text-ink">Available</label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="popular"
            checked={formData.popular}
            onChange={handleChange}
            className="mr-2"
          />
          <label className="font-semibold text-ink">Popular</label>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Create Item"}
        </button>
      </form>
    </div>
  );
};

export default CreateItem;
